import { trackUserEvent, trackEventNoAuth } from 'src/graphql/event';
import { captureMessage } from 'src/utils/lib/sentry';

export const PROJECT = {
  variationCopy: 'project.variation.copy',
  exportSaved: 'project.saved.exportCSV',
  saveFromRecompose: 'project.own_text.save',
  blogPostSubmitDraft: 'project.blog_post.clicked_continueToEditor',
  blogPostCopy: 'project.blog_post.copy',
  blogPostRephrase: 'project.blog_post.rephrase',
  blogPostAcceptRephrase: 'project.blog_post.accept_rephrase',
  blogOpenInsightsPanel: 'project.blog_post.open_insights_panel',
  blogInsightsPanelExpandSeoTracker: 'project.blog_post.insights_panel.expand_seo_tracker',
  blogInsightsPanelExpandBrandRules: 'project.blog_post.insights_panel.expand_brand_rules',
  blogInsightsBrandRuleApply: 'project.blog_post.insights_panel.brand_rule_apply',
  copySectionContent: 'project.blog_post.copy_section_content',
  enteredProject: 'project.entered_project',
  clickedVariationDetails: 'project.variation.clicked_details',
  clickedRecomposeDetails: 'project.variation.clicked_details_recompose',
  clickedExplainerVideo: 'project.clicked_explainer_video',
  clickedVariationAddTemplateOnDetails: 'project.variation.score_panel.clicked_add_to_templates',
  clickedRecomposeAddTemplateOnDetails: 'project.recompose.score_panel.clicked_add_to_templates',
  briefUpdate: 'project.brief_update',
  clickedDocumentRephraseText: 'project.document.rephrase_text',
  clickedDocumentAcceptRephraseText: 'project.document.accept_rephrase_text',
  clickedDocumentImproveScore: 'project.document.improve_score',
  clickedDocumentAcceptImproveScoreResult: 'project.document.accept_improve_score_result',
  clickedScoreHintClose: 'project.onboarding.clicked_close_score_hint',
  checkedScoreHintDontShow: 'project.onboarding.checked_score_hint_dont_show',
  clickAssetActionsHintClose: 'project.onboarding.clicked_close_asset_actions_hint',
  checkedAssetActionsHintDontShow: 'project.onboarding.checked_asset_actions_hint_dont_show',

  // Events for "locked score" views
  clickedLockedGetScore: 'project.locked_score.get_score.clicked',
  clickedLockedUndo: 'project.locked_score.undo.clicked',
  clickedLockedImproveScore: 'project.improve_score.locked.clicked',
  clickedLockedUpgradeToDD: 'project.locked_score.upgrade_to_dd.clicked',

  // Events for "score dataset" views
  clickedScoreDatasetLink: 'project.score_dataset.score_dataset_link.clicked',
  clickedScoreCopyScoringModelTab: 'project.score_dataset.custom_dataset_tab.clicked',
  applyScoreCopyScoringModel: 'project.score_dataset.custom_dataset.applied',

  clickedGenerateFromSectionPanel: 'project.generate_panel.generate_button.clicked',
  clickedGenerateWithInstructionsFromURL:
    'project.generate_panel.generate_button.instructionsFromURL',
  clickedGenerateWithInstructionsFromParagraph:
    'project.generate_panel.generate_button.instructionsFromParagraph',
  clickedOpenImportInstructionsFromURL: 'project.import_instructions.clicked_openPopup',
  approveInstructionsFromURL: 'project.import_instructions.approve_instructions',
  generateAllSectionsClicked: 'project.generate_all_sections.clicked',
  generateAllSectionsGenerated: 'project.generate_all_sections.generated',
};

export const DOCUMENT = {
  clickedTargetAudience: 'document.target_audience.clicked',
  clickedToneOfVoice: 'document.tone_of_voice.clicked',
  addedTalkingPoint: 'document.talking_points.added',
  viewedHistoryResultDetails: 'document.history.viewed_result_details',
  shareClicked: 'document.share_button.clicked',

  variationAddToDocumentClicked: 'document.variation.add_to_document.clicked',
  variationImproveScoreClicked: 'document.variation.improve_score.clicked',
  variationImproveScoreAcceptedClicked: 'document.variation.improve_score_accepted.clicked',
  variationScoreThisTextClicked: 'document.variation.score_this_text_clicked',
  variationImproveScoreUsingDefaultSuggestion:
    'document.variation.improve_score.default_suggestion',
  variationImproveScoreUsingGeneratedSuggestion:
    'document.variation.improve_score.generated_suggestion',
  variationImproveScoreUsingRemovedTalkingPoints:
    'document.variation.improve_score.removed_talking_points',
  variationManualInstructionsGeneration: 'document.variation.manual_instructions.generation',
  variationEditClicked: 'document.variation.edit.clicked',
  variationCopyClicked: 'document.variation.copy.clicked',
  variationBoostPerformance: 'document.variation.boost_performance',

  annotationExtractedFromUrl: 'document.annotation.extracted_from_url',
  annotationExtractedFromBrief: 'document.annotation.extracted_from_brief',

  openedGoogleImportAd: 'document.opened_google_import_ad',
  openedFBImportAd: 'document.opened_fb_import_ad',

  savedGoogleImportAd: 'document.saved_google_import_ad',
  savedFBImportAd: 'document.saved_fb_import_ad',

  openedGoogleImportExamples: 'document.opened_google_import_examples',
  openedFBImportExamples: 'document.opened_fb_import_examples',

  savedGoogleImportExamples: 'document.saved_google_import_examples',
  savedFBImportExamples: 'document.saved_fb_import_examples',

  freestyleSaveInstructions: 'document.freestyle.save_instructions',
  freestyleSuggestPromptClicked: 'document.freestyle.suggest_prompt_clicked',
  freestyleGenerateWithSuggestedPrompt: 'document.freestyle.generate_with_suggested_prompt',

  clickedGenerateRecompose: 'document.recompose.generate_button.clicked',
  clickedGenerateSuggest: 'document.suggest.generate_button.clicked',
  generateWithCallToAction: 'document.generate_with_call_to_action',

  briefImportedFromUrl: 'document.briefs.brief_imported_from_url',
  brandBriefLoaded: 'document.briefs.brand_brief_loaded',
};

export const WTM = {
  projectCreated: 'wtm.project_created',
  generateMore: 'wtm.generate_more.clicked',
  addYourOwn: 'wtm.add_your_own.clicked',
  addToCampaign: 'wtm.add_to_campaign.clicked',
  verifySetup: 'wtm.verify_setup.clicked',
  runCampaign: 'wtm.run_campaign.clicked',
  pauseCampaign: 'wtm.pause_campaign.clicked',
  createWtmVisited: 'wtm.create_wtm.visited',
  gatedWtmVisited: 'wtm.gated_wtm.visited',
};

export const PURCHASE = {
  navbarPurchaseClick: 'purchase_flow.clicked_purchase_navbar',
  settingsPurchaseClick: 'purchase_flow.clicked_purchase_settings',
  trialEndedPurchaseClick: 'purchase_flow.clicked_purchase_trial_ended',
  subscriptionPageOpened: 'purchase_flow.opened_subscription_page',
  tiersDropdownOpened: 'purchase_flow.clicked_tiers_dropdown',
  confirmTier: 'purchase_flow.clicked_tiers_cta_button',
  abandonedSubscriptionPage: 'purchase_flow.abandoned_subscription_page',
  paymentFormEdit: 'purchase_flow.edited_cc_form',
  confirmPayment: 'purchase_flow.clicked_cc_purchase',
  completedPurchase: 'purchase_flow.completed_purchase',
};

export const MODIFY_PLAN = {
  settingsModifyClick: 'modify_flow.clicked_modify_settings',
  subscriptionPageOpened: 'modify_flow.opened_subscription_page',
  tiersDropdownOpened: 'modify_flow.clicked_tiers_dropdown',
  confirmUpgrade: 'modify_flow.clicked_confirm_upgrade',
  confirmDowngrade: 'modify_flow.clicked_confirm_downgrade',
  abandonedSubscriptionPage: 'modify_flow.abandoned_subscription_page',
};

export const BUSINESS_DEMO = {
  businessDemoBooked: 'business_demo.booked',
  businessDemoRequested: 'business_demo.requested',
};

export const QUOTA_ENDED = {
  popupOpened: 'no_words_popup.opened_popup',
  popupCancelled: 'no_words_popup.abandoned_popup',
  clickedOverage: 'no_words_popup.clicked_get_overage',
  confirmOverage: 'no_words_popup.clicked_confirm_overage_purchase',
  clickedUpgrade: 'no_words_popup.clicked_upgrade',
  confirmUpgrade: 'no_words_popup.clicked_confirm_upgrade',
};

export const SETTINGS = {
  openedPaymentDetailsPage: 'settings.payment_method.opened_editing_page',
  savedPaymentMethodChanges: 'settings.payment_method.saved_changes',
  clickedInvite: 'settings.invite.clicked',
  clickedSendInvitation: 'settings.invite.clicked_send',
  clickedUserProfile: 'settings.user_profile.clicked',
  changeUserRole: 'settings.user_role.change',
  clickedRemoveUser: 'settings.user.clicked_remove',
  switchToAnnuallyViewMoreClicked: 'settings.switch_to_annually.view_more.clicked',
};

export const NOTIFICATIONS = {
  clickedCtaButton: 'notification.clicked_CTA_button',
  clickedClose: 'notification.clicked_close',
};

export const SYSTEM = {
  gtmInaccessible: 'system.gtm_inaccessible',
  navigationItemClicked: 'system.navigation.item_clicked',
};

export const WORKSPACES = {
  clickedExplainerVideo: 'workspaces.clicked_explainer_video',
  clickedWebinarSignup: 'workspace.clicked_webinar_signup',
};

export const PERSONAS = {
  clickedExplainerVideo: 'personas.clicked_explainer_video',
};

export const SUGGESTED_AUDIENCES = {
  generation: 'suggested_audience.generation',
  saved: 'suggested_audience.saved',
  edited: 'suggested_audience.edited',
  deleted: 'suggested_audience.deleted',
};

export const AUTH = {
  disposableEmailBlocked: 'auth.disposable_email_blocked',
};

export const SHARING = {
  changedSharedPermissions: 'share_popup.changed_share_permissions',
  copyLinkClicked: 'share_popup.copy_link_clicked',
  viewedSharedDocument: 'shared_page.document_viewed',
  sharedSignUpClicked: 'shared_page.sign_up_button.clicked',
  sharedShareClicked: 'shared_page.share_button.clicked',
  sharedExportClicked: 'shared_page.csv_export.clicked',
};

export const TRACK_EVENT_SOURCE = {
  settings: 'settings',
  navbar: 'navbar',
};

export const ANYCOPY_WIDGET = {
  suggestionClick: 'anycopy_widget.suggestion.clicked',
  copyClick: 'anycopy_widget.copy.clicked',
};

export const APP = {
  currentUserInitialRequestCompleted: 'app.current_user_initial_request.completed',
  welcomePopupProjectTypeSelected: 'app.welcome_popup.project_type_selected',
};

export const BRAND_TERMS = {
  ruleCreated: 'brand_terms.rules.created',
  ruleUpdated: 'brand_terms.rules.updated',
  ruleToggled: 'brand_terms.rules.toggled',
  ruleDeleted: 'brand_terms.rules.deleted',
  detectedTermApplied: 'brand_terms.detected_term_applied',
  pageVisited: 'brand_terms.page.visited',
};

export const BRAND_BRIEFS = {
  pageVisited: 'brand_briefs.page.visited',
  briefCreated: 'brand_briefs.created',
  briefUpdated: 'brand_briefs.updated',
};

export const CUSTOM_FORMULAS = {
  pageVisited: 'custom_formulas.page.visited',
  formulaCreated: 'custom_formulas.created',
  formulaUpdated: 'custom_formulas.updated',
  formulaExtracted: 'custom_formulas.extracted',
};

export const CUSTOM_TONE_OF_VOICE = {
  pageVisited: 'custom_tone_of_voice.page.visited',
  customToneOfVoiceCreated: 'custom_tone_of_voice.created',
  customToneOfVoiceUpdated: 'custom_tone_of_voice.updated',
  customToneOfVoiceExtracted: 'custom_tone_of_voice.extracted',
};

export const TARGET_AUDIENCE = {
  pageVisited: 'target_audience.page.visited',
  targetAudienceExtracted: 'target_audience.extracted',
  targetAudienceCreated: 'target_audience.created',
  targetAudienceUpdated: 'target_audience.updated',
};

export const CIP = {
  toggleTable: 'cip.table.toggled',
  connectAdAccounts: 'cip.accounts.connected',
  improveButtonClick: 'cip.improve_button.clicked',
  connectedChannelsPageVisited: 'cip.connected_channels.page.visited',
  talkingPointsPageVisited: 'cip.talking_points.page.visited',
  customScoringModelPageVisited: 'cip.custom_scoring_model.page.visited',
  filterApplied: 'cip.filter.applied',
  createScoringModel: 'cip.scoringModel.created',
  reviewsAccountConnected: 'cip.reviews_account.connected',
  websiteAccountsConnected: 'cip.website_accounts.connected',
};

export const INFINITY = {
  clickedPromptCategory: 'infinity.prompt.category_clicked',
  insertedPromptComponent: 'infinity.prompt.component_inserted',
  selectedTalkingPointsChannel: 'infinity.prompt.talking_points_channel_selected',
  toggledBrandVoice: 'infinity.prompt.brand_voice.toggled',
  selectedToneOfVoice: 'infinity.prompt.tone_of_voice_selected',
  toggledBrandRules: 'infinity.prompt.brand_rules_toggled',
  clickedManageLink: 'infinity.prompt.manage_link_clicked',
};

/**
 * Adds the `infinity.` prefix to the event name
 * @param {string} name - the event name
 * @returns {string} - the event name with the prefix
 */
export const prefixExtensionEventName = (name) => `infinity.${name}`;

export function trackEvent(name, incomingAttributes = null) {
  const attributes = {
    ...incomingAttributes,
    eventCategory: parseEventCategory(name),
  };

  return trackUserEvent(name, attributes).catch((error) => {
    captureMessage('Error tracking FE event', {
      level: 'warning',
      extra: {
        error: error.toString(),
        source: 'lib/events/platform -> trackEvent',
        eventName: name,
        attributes,
      },
    });
  });
}

export function trackUserEventNoAuth(name, incomingAttributes = null) {
  const attributes = {
    ...incomingAttributes,
    eventCategory: parseEventCategory(name),
  };

  return trackEventNoAuth(name, attributes).catch((error) => {
    captureMessage('Error tracking FE event', {
      level: 'warning',
      extra: {
        error: error.toString(),
        source: 'lib/events/platform -> trackEventNoAuth',
        eventName: name,
        attributes,
      },
    });
  });
}

const parseEventCategory = (name) => {
  return name?.split('.')[0] || '';
};

export const getCdNamesValues = (dimensions) => {
  if (!dimensions || Object.keys(dimensions).length === 0) {
    return null;
  }

  // check that dimensions are flat and include only strings / numbers / bool
  const wrongDimensionTypes = Object.values(dimensions).filter(
    (value) => !['string', 'number', 'boolean'].includes(typeof value)
  );
  if (wrongDimensionTypes.length) {
    captureMessage('Error with FE event dimensions', {
      level: 'error',
      extra: {
        error:
          'Problem with dimensions types. Dimensions must be of type string / number / boolean and non nested.',
        source: 'lib/events/platform -> getCdNamesValues',
        dimensions: dimensions,
      },
    });
    console.error(
      'Problem with dimensions types. Dimensions must be of type string / number / boolean and non nested.'
    );
    return null;
  }

  const cdNames = Object.keys(dimensions);
  const cdValues = Object.values(dimensions);

  return { cdNames, cdValues };
};

window.trackInternalEvent = trackEvent;
